import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Cpu } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const TOOLTIP_STYLE = { background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: '4px', color: 'hsl(var(--foreground))', fontSize: '12px' };
const AXIS_TICK = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' };
const BAR_RADIUS = [2, 2, 0, 0];

const tooltipFormatter = (value, name) => {
  if (name === 'calls') return [value, 'API Calls'];
  if (name === 'cost') return [`$${value.toFixed(4)}`, 'Cost'];
  return [value, name];
};

export default function UsagePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUsage = useCallback(() => {
    api.get('/settings/api-usage')
      .then(res => setData(res.data))
      .catch(err => void 0)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  const breakdown = useMemo(() => data?.user_breakdown || [], [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="usage-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Usage</h1>
        <p className="text-muted-foreground text-sm mt-1">AI API usage and cost tracking</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="border border-border rounded-md p-5 text-center"><p className="font-mono text-2xl font-bold">{data.today_calls}</p><p className="text-xs uppercase tracking-wider text-muted-foreground mt-1">Today</p></div>
        <div className="border border-border rounded-md p-5 text-center"><p className="font-mono text-2xl font-bold">{data.month_calls}</p><p className="text-xs uppercase tracking-wider text-muted-foreground mt-1">This Month</p></div>
        <div className="border border-border rounded-md p-5 text-center"><p className="font-mono text-2xl font-bold">{data.total_calls}</p><p className="text-xs uppercase tracking-wider text-muted-foreground mt-1">All Time</p></div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="border border-border rounded-md p-5">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">This Month</p>
          <p className="font-mono text-2xl font-bold text-[hsl(var(--accent))]">${data.month_cost_usd?.toFixed(4) || '0.0000'}</p>
          <p className="text-xs text-muted-foreground mt-1 font-mono">{(data.month_tokens || 0).toLocaleString()} tokens</p>
        </div>
        <div className="border border-border rounded-md p-5">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">All Time</p>
          <p className="font-mono text-2xl font-bold">${data.total_cost_usd?.toFixed(4) || '0.0000'}</p>
          <p className="text-xs text-muted-foreground mt-1 font-mono">{(data.total_tokens || 0).toLocaleString()} tokens</p>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Images processed: <span className="font-mono font-semibold text-foreground">{data.total_images_processed}</span> &middot; Cost estimated at GPT-4o-mini rates
      </p>

      {data.daily_breakdown?.length > 0 && (
        <div className="border border-border rounded-md p-6">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">Last 7 Days</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.daily_breakdown}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={AXIS_TICK} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={AXIS_TICK} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={tooltipFormatter} />
              <Bar dataKey="calls" fill="hsl(var(--accent))" radius={BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="border border-border rounded-md overflow-hidden" data-testid="user-usage-table">
        <div className="p-6 border-b border-border flex items-center gap-2">
          <Cpu className="w-4 h-4 text-[hsl(var(--accent))]" />
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Usage by User</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">User</th>
                <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold hidden sm:table-cell">Source</th>
                <th className="text-right px-5 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Receipts</th>
                <th className="text-right px-5 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">API Calls</th>
                <th className="text-right px-5 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold hidden md:table-cell">Tokens</th>
                <th className="text-right px-5 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {breakdown.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-muted-foreground text-sm">No usage data yet</td></tr>
              ) : breakdown.map((u) => (
                <tr key={`${u.name}-${u.sender}`} className="hover:bg-secondary/30 transition-colors">
                  <td className="px-5 py-3"><p className="text-sm font-medium">{u.name || u.sender || 'Unknown'}</p></td>
                  <td className="px-5 py-3 text-sm text-muted-foreground font-mono hidden sm:table-cell">{u.sender || '—'}</td>
                  <td className="px-5 py-3 text-sm font-mono text-right font-semibold">{u.receipts_processed}</td>
                  <td className="px-5 py-3 text-sm font-mono text-right">{u.api_calls}</td>
                  <td className="px-5 py-3 text-sm font-mono text-right text-muted-foreground hidden md:table-cell">{u.tokens.toLocaleString()}</td>
                  <td className="px-5 py-3 text-sm font-mono text-right">${u.cost.toFixed(4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
